import { Button, Card, Classes, Elevation, FormGroup, Icon, InputGroup, Switch } from '@blueprintjs/core'
import { toast } from 'botpress/shared'
import React, { FC, useEffect, useState } from 'react'
import api from '~/app/api'

interface Props {
  botId: string
}

interface BotRegisterScreenConfig {
  enabled: boolean
  header: string
  subtitle: string
  backgroundColor: string
  textFieldBackgroundColor: string
  textFieldTextColor: string
  textFieldBorderColor: string
  buttonText: string
  buttonBackgroundColor: string
  buttonTextColor: string
  emailSetup: {
    emailId: string
    appPass: string
    host: string
    port: string
  }
}

const defaultConfig: BotRegisterScreenConfig = {
  enabled: true,
  header: 'Welcome!',
  subtitle: 'Please enter your contact info to start chatting with us',
  backgroundColor: '#667eea',
  textFieldBackgroundColor: '#fafafa',
  textFieldTextColor: '#333333',
  textFieldBorderColor: '#e0e0e0',
  buttonText: 'Send Verification Code',
  buttonBackgroundColor: '#667eea',
  buttonTextColor: '#ffffff',
  emailSetup: {
    emailId: '',
    appPass: '',
    host: '',
    port: '587'
  }
}

const DEFAULT_REGISTER_SUBTITLE = 'Please enter your contact info to start chatting with us'
const DEFAULT_START_CHAT_SUBTITLE = 'Click the button below to start chatting with us'


const SMTP_HOST_OPTIONS = [
  { value: 'gmail', label: 'Gmail (@gmail.com)' },
  { value: 'outlook', label: 'Outlook (@outlook.com)' },
  { value: 'hotmail', label: 'Hotmail (@hotmail.com)' },
  { value: 'live', label: 'Live (@live.com)' },
  { value: 'office365', label: 'Office 365 (@office365.com)' },
  { value: 'zoho', label: 'Zoho (@zoho.com)' }
]

const SMTP_PORT_OPTIONS = [
  { value: '587', label: '587 (STARTTLS)' },
  { value: '465', label: '465 (SSL)' }
]

const ColorInput: FC<{
  label: string
  value: string
  helperText?: string
  onChange: (value: string) => void
}> = ({ label, value, helperText, onChange }) => (
  <FormGroup label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>{label}</span>} helperText={helperText}>
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      <input
        type="color"
        value={value}
        onChange={event => onChange(event.target.value)}
        style={{ width: '50px', height: '36px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px' }}
      />
      <InputGroup value={value} onChange={event => onChange(event.target.value)} placeholder="#FFFFFF" />
    </div>
  </FormGroup>
)

const TextInput: FC<{
  label: string
  value: string
  placeholder: string
  helperText?: string
  maxLength?: number
  type?: string
  isPassword?: boolean
  rightElement?: JSX.Element
  onChange: (value: string) => void
}> = ({ label, value, placeholder, helperText, maxLength = 80, type = 'text', isPassword = false, rightElement, onChange }) => {
  const [showPassword, setShowPassword] = useState(false)

  const effectiveType = isPassword ? (showPassword ? 'text' : 'password') : type
  const effectiveRightElement = isPassword ? (
    <Button
      icon={showPassword ? 'eye-open' : 'eye-off'}
      minimal={true}
      onClick={() => setShowPassword(!showPassword)}
    />
  ) : (
    rightElement
  )

  return (
    <FormGroup label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>{label}</span>} helperText={helperText}>
      <InputGroup
        type={effectiveType}
        value={value}
        onChange={event => onChange(event.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        rightElement={effectiveRightElement}
      />
    </FormGroup>
  )
}

const SelectInput: FC<{
  label: string
  value: string
  options: { value: string; label: string }[]
  helperText?: string
  onChange: (value: string) => void
}> = ({ label, value, options, helperText, onChange }) => {
  const hasCurrentValue = !value || options.some(option => option.value === value)
  const renderedOptions = hasCurrentValue ? options : [{ value, label: value }, ...options]

  return (
    <FormGroup label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>{label}</span>} helperText={helperText}>
      <select value={value} onChange={event => onChange(event.target.value)} className={Classes.INPUT} style={{ width: '100%' }}>
        {renderedOptions.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FormGroup>
  )
}

const BotRegisterScreenCustomization: FC<Props> = ({ botId }) => {
  const [config, setConfig] = useState<BotRegisterScreenConfig>(defaultConfig)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    void loadConfig()
  }, [botId])

  const loadConfig = async () => {
    try {
      setIsLoading(true)
      const { data } = await api.getSecured().get(`/admin/workspace/bots/${botId}/module-config/channel-web`)
      const existingConfig = data?.payload || data || {}

      setConfig({
        ...defaultConfig,
        ...existingConfig.botRegisterScreen,
        emailSetup: {
          ...defaultConfig.emailSetup,
          ...existingConfig.botRegisterScreen?.emailSetup
        }
      })
    } catch (error) {
      console.error('Failed to load bot register screen config:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const saveConfig = async () => {
    try {
      setIsSaving(true)
      await api.getSecured().post(`/admin/workspace/bots/${botId}/module-config/channel-web`, {
        botRegisterScreen: config
      })
      toast.success('Bot register screen customization saved successfully!')
    } catch (error: any) {
      console.error('Failed to save bot register screen config:', error)
      toast.failure(`Failed to save configuration: ${error.message || 'Unknown error'}`)
    } finally {
      setIsSaving(false)
    }
  }

  const updateConfig = (value: Partial<BotRegisterScreenConfig>) => {
    setConfig(current => ({
      ...current,
      ...value
    }))
  }

  const updateEmailSetup = (value: Partial<BotRegisterScreenConfig['emailSetup']>) => {
    setConfig(current => ({
      ...current,
      emailSetup: {
        ...current.emailSetup,
        ...value
      }
    }))
  }

  const subtitlePlaceholder = config.enabled ? DEFAULT_REGISTER_SUBTITLE : DEFAULT_START_CHAT_SUBTITLE
  const previewSubtitle = config.subtitle || subtitlePlaceholder

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 42px', borderRight: '1px solid #e1e4e8' }}>
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
            Bot Register Screen Customization
          </h4>
          <p style={{ margin: 0, fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
            Customize the registration screen shown before a user starts chatting.
          </p>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            <Icon icon="refresh" iconSize={24} />
            <p style={{ marginTop: '12px' }}>Loading configuration...</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gap: '24px' }}>
              <Card elevation={Elevation.ONE} style={{ padding: '20px', backgroundColor: '#f8f9fa' }}>
                <h5
                  style={{
                    margin: '0 0 16px 0',
                    fontSize: '15px',
                    fontWeight: 600,
                    color: '#333',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Icon icon="cog" iconSize={16} color="#5C7CFA" />
                  Settings
                </h5>

                <Switch
                  checked={config.enabled}
                  label="Enable registration flow (name/phone/email)"
                  onChange={event => updateConfig({ enabled: (event.target as HTMLInputElement).checked })}
                />
                <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.6', marginTop: '6px' }}>
                  When disabled, users will only see a "Start Chat" button before entering the chat.
                </div>
              </Card>

              <Card elevation={Elevation.ONE} style={{ padding: '20px', backgroundColor: '#f8f9fa' }}>
                <h5 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon="header" iconSize={16} color="#5C7CFA" />
                  Header
                </h5>

                <TextInput label="Header" value={config.header} placeholder="Welcome!" maxLength={70} onChange={header => updateConfig({ header })} />
                <TextInput label="Subtitle" value={config.subtitle} placeholder={subtitlePlaceholder} maxLength={140} onChange={subtitle => updateConfig({ subtitle })} />
                <ColorInput label="Background Color" value={config.backgroundColor} helperText="Background color for the register screen" onChange={backgroundColor => updateConfig({ backgroundColor })} />
              </Card>

              {config.enabled && (
                <>
                  <Card elevation={Elevation.ONE} style={{ padding: '20px', backgroundColor: '#f8f9fa' }}>
                    <h5 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Icon icon="form" iconSize={16} color="#5C7CFA" />
                      Text Fields
                    </h5>
                    <ColorInput label="Field Background Color" value={config.textFieldBackgroundColor} onChange={textFieldBackgroundColor => updateConfig({ textFieldBackgroundColor })} />
                    <ColorInput label="Field Text Color" value={config.textFieldTextColor} onChange={textFieldTextColor => updateConfig({ textFieldTextColor })} />
                    <ColorInput label="Field Border Color" value={config.textFieldBorderColor} onChange={textFieldBorderColor => updateConfig({ textFieldBorderColor })} />
                  </Card>
                </>
              )}

              <Card elevation={Elevation.ONE} style={{ padding: '20px', backgroundColor: '#f8f9fa' }}>
                <h5 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon="manually-entered-data" iconSize={16} color="#5C7CFA" />
                  Button
                </h5>


                {config.enabled && (
                  <TextInput label="Button Text" value={config.buttonText} placeholder="Send Verification Code" maxLength={40} onChange={buttonText => updateConfig({ buttonText })} />
                )}
                <ColorInput label="Button Background Color" value={config.buttonBackgroundColor} onChange={buttonBackgroundColor => updateConfig({ buttonBackgroundColor })} />
                <ColorInput label="Button Text Color" value={config.buttonTextColor} onChange={buttonTextColor => updateConfig({ buttonTextColor })} />
              </Card>


              {config.enabled && (
                <>
                  <Card elevation={Elevation.ONE} style={{ padding: '20px', backgroundColor: '#f8f9fa' }}>
                    <h5 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Icon icon="envelope" iconSize={16} color="#5C7CFA" />
                      Email Setup
                    </h5>


                    <div style={{ display: 'flex', gap: '10px', padding: '12px', marginBottom: '16px', borderRadius: '4px', backgroundColor: '#E8F4FD', borderLeft: '4px solid #2196F3' }}>
                      <Icon icon="info-sign" iconSize={16} color="#2196F3" style={{ marginTop: '2px', flexShrink: 0 }} />
                      <div style={{ fontSize: '12px', color: '#333', lineHeight: '1.6' }}>
                        The success mail after registration will be sent from this given emailId. If no email setup is provided.
                      </div>
                    </div>


                    <TextInput label="Email ID" value={config.emailSetup.emailId} placeholder="your@email.com" maxLength={120} onChange={emailId => updateEmailSetup({ emailId })} />
                    <TextInput label="App Password" value={config.emailSetup.appPass} placeholder="App password" isPassword={true} maxLength={120} onChange={appPass => updateEmailSetup({ appPass })} />
                    <SelectInput label="Host" value={config.emailSetup.host} options={SMTP_HOST_OPTIONS} onChange={host => updateEmailSetup({ host })} />
                    <SelectInput label="Port" value={config.emailSetup.port} options={SMTP_PORT_OPTIONS} onChange={port => updateEmailSetup({ port })} />
                  </Card>
                </>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e1e4e8' }}>
              <Button text="Reset to Defaults" onClick={() => setConfig(defaultConfig)} icon="refresh" />
              <Button text={isSaving ? 'Saving...' : 'Save Changes'} onClick={saveConfig} intent="primary" icon="floppy-disk" disabled={isSaving} loading={isSaving} />
            </div>
          </>
        )}
      </div>

      <div className="modern-scrollbar" style={{ width: '500px', flexShrink: 0, backgroundColor: '#f8f9fa', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        <div style={{ padding: '10px', borderBottom: '1px solid #e1e4e8', backgroundColor: '#fff' }}>
          <h5 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>
            Live Preview
          </h5>
        </div>

        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', minHeight: 0 }}>
          <div style={{ width: '380px', height: '100%', maxHeight: '460px', padding: '16px', background: config.backgroundColor, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px', boxSizing: 'border-box' }}>
            <div className="modern-scrollbar" style={{ width: '100%', maxHeight: '100%', overflowY: 'auto', background: '#ffffff', borderRadius: '8px', padding: '20px 18px', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxSizing: 'border-box' }}>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', lineHeight: 1.2, fontWeight: 700, color: '#333333', fontFamily: 'Roboto, sans-serif', wordBreak: 'break-word' }}>
                  {config.header}
                </h3>
                <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.4, color: '#666666', fontFamily: 'Roboto, sans-serif', wordBreak: 'break-word' }}>
                  {previewSubtitle}
                </p>
              </div>

              <div style={{ marginBottom: '16px' }}>
                {config.enabled &&
                  ['Your name', 'Phone number', 'your.email@example.com'].map((placeholder, index) => (
                    <input
                      key={placeholder}
                      readOnly
                      value=""
                      placeholder={placeholder}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        marginBottom: index === 2 ? 0 : '8px',
                        borderRadius: '6px',
                        border: `1.5px solid ${config.textFieldBorderColor}`,
                        backgroundColor: config.textFieldBackgroundColor,
                        color: config.textFieldTextColor,
                        fontSize: '13px',
                        fontFamily: 'Roboto, sans-serif',
                        outline: 'none'
                      }}
                    />
                  ))}
              </div>

              <button
                type="button"
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  background: config.buttonBackgroundColor,
                  color: config.buttonTextColor,
                  fontSize: '14px',
                  fontWeight: 600,
                  fontFamily: 'Roboto, sans-serif',
                  boxShadow: '0 4px 10px rgba(102, 126, 234, 0.2)',
                  cursor: 'default'
                }}
              >
                {config.enabled ? config.buttonText : 'Start Chat'}
              </button>

              {config.enabled && (
                <div style={{ textAlign: 'center', marginTop: '14px' }}>
                  <p style={{ margin: 0, fontSize: '11px', color: '#999999', fontFamily: 'Roboto, sans-serif' }}>
                    Your data will be kept private and secure
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}


export default BotRegisterScreenCustomization
